/*
 * Copyright (c) 2022. Revo Digital
 * ---
 * Author: gabrielecavallo
 * File: PanFeature.ts
 * Project: mle-client-n
 * Committed last: 2022/11/22 @ 166
 * ---
 * Description:
 */

import {Feature} from '../common/Feature';
import Pamela from '@revodigital/pamela';
import {Editor} from '../../observables/Editor';
import {action, computed, makeObservable, observable} from 'mobx';
import {Point2D, pointOf} from '@revodigital/pamela/lib/common/Point2D';

export const PAN_FNAME = 'pan';

export enum PanDirection {
  Up,
  Down,
  Left,
  Right
}

export class PanFeature extends Feature<Pamela.Stage, any, Editor> {
  @observable _position: Point2D;

  private defaultPosition: Point2D;

  constructor(parent: Editor) {
    super(PAN_FNAME,
        parent.pamelaStage__nonObservable,
        parent, true);

    this.defaultPosition = pointOf(50, 70);
    this._position = new Point2D(this.defaultPosition.x,
      this.defaultPosition.y);

    // Make us observable
    makeObservable(this);
  }

  @computed
  public get position() {
    return this._position;
  }

  @action
  public setPosition(pos: Point2D) {
    this._position = pos;
    this.target.absolutePosition(this._position);
  }

  @action
  public moveViewLeft(amount: number = 20) {
    // TODO: Refactor utilizzando action invece di azioni dirette
    this._position = pointOf(this._position.x + amount, this._position.y);
    this.target.absolutePosition(this._position);
  }

  @action
  public moveViewRight(amount: number = 20) {
    this._position = pointOf(this._position.x - amount, this._position.y);
    this.target.absolutePosition(this._position);
  }

  @action
  public moveViewUp(amount: number = 20) {
    this._position = pointOf(this._position.x, this._position.y + amount);
    this.target.absolutePosition(this._position);
  }

  @action
  public moveViewDown(amount: number = 20) {
    this._position = pointOf(this._position.x, this._position.y - amount);
    this.target.absolutePosition(this._position);
  }

  /**
   * Moves the view according to the direction
   * @param direction Direction to move
   */
  @action
  public moveView(direction: PanDirection) {
    switch (direction) {
      case PanDirection.Down:
        this.moveViewDown();
        break;
      case PanDirection.Up:
        this.moveViewUp();
        break;
      case PanDirection.Left:
        this.moveViewLeft();
        break;
      case PanDirection.Right:
        this.moveViewRight();
        break;
    }
  }

  /**
   * Inquadra una forma per renderla completamente visibile nell'area
   * di lavoro
   * @param shape Forma da inquadrare
   */
  public frameShape(shape: Pamela.Shape) {
    const viewPortSize = this.target.getSizeRect();
    const shapeSize = shape.getSizeRect();
    const scale = this.target.scaleX();
    const w = shape.width();
    const h = shape.height();

    if (!shapeSize.overflows(viewPortSize)) {
      const l = (viewPortSize.getWidth() - w) / 2;
      const m = (viewPortSize.getHeight() - h) / 2;
      const x = ((shape.x()) - ((1 - scale) * shape.x())) * -1;
      const y = (shape.y() - (1 - scale) * shape.y()) * (-1)
      this.setPosition(pointOf(x + l, y + m));
    } else {
      const k = 30;
      const s = Math.max((viewPortSize.getWidth() - (2 * k)) / shape.width(),
        (viewPortSize.getHeight() - (2 * k)) / shape.height());

      const x = ((shape.x()) - ((1 - scale) * shape.x())) * -1;
      const y = (shape.y() - (1 - scale) * shape.y()) * (-1)
      this.setPosition(pointOf(x + k, y + k));
      this.sourcePointer.zoom().setZoom(s / 2);
    }
  }

  @action
  public reset() {
    this._position = new Point2D(this.defaultPosition.x,
      this.defaultPosition.y);
    this.target.absolutePosition(this._position);
  }

  // TODO: Implement destroy
  destroy(): boolean {
    return false;
  }

  init(): boolean {
    // Enable panning for the stage
    this.target.draggable(true);
    this.target.dragbuttons([ { button: 1 }, { button: 0, altKey: true } ]);

    // Set default view position
    this.target.setAbsolutePosition(this.defaultPosition.toVector());
    Pamela.dragButtons = [ { button: 0 } ];
    let th = this;

    this.target.on('dragstart', (event) => {
      th.target.container().style.cursor = 'move';
      event.evt?.stopImmediatePropagation();
      event.evt?.preventDefault();
    });

    // Update pan position for the state
    this.target.on('dragend', (event) => {
      // Update stage position
      if (event.target === th.target)
        th._position = Point2D.fromVector((event.target as Pamela.Stage).absolutePosition());

      // Update cursor
      th.target.container().style.cursor = 'default';
    });
    return true;
  }
}
